import CrudApi from "@serie3/common/api/crudApi";
import {
  uploadInChunks,
  discardUpload,
  createUploadQueue,
} from "@serie3/common/api/chunkedUpload";
import ToastMessages from "../../../ToastMessages";

/**
 * Page-wide upload state. Uploads are started here, not in the dropzone, so
 * they survive the upload modal being closed: the dropzone (and the floating
 * UploadStatusWidget) only render what this store holds. Every entry keeps
 * its File in memory, which is what makes retry possible.
 *
 * Nothing here survives a full page unload: a File cannot be persisted. The
 * names of uploads that were still running are kept in localStorage so the
 * next visit can at least tell the user what was lost (see readInterrupted).
 */

export const STATUS = {
  QUEUED: "queued",
  UPLOADING: "uploading",
  SAVING: "saving",
  DONE: "done",
  FAILED: "failed",
  TOO_BIG: "toobig",
  CANCELLED: "cancelled",
};

export const isActive = (entry) =>
  entry.status === STATUS.QUEUED ||
  entry.status === STATUS.UPLOADING ||
  entry.status === STATUS.SAVING;

export const isFinished = (entry) => !isActive(entry);

const INTERRUPTED_KEY = "mmp.uploads.interrupted";
const PREVIEWABLE = ["image/jpeg", "image/png", "image/gif", "image/webp", "text/plain", "application/pdf"];

/** Two files in flight at a time; the rest wait. Shared by every dropzone on the page. */
const runQueue = createUploadQueue(2);

let entries = [];
let nextId = 1;
const listeners = new Set();
const controllers = new Map();
const targets = new Map();

// ---- store plumbing ---------------------------------------------------------

const onBeforeUnload = (event) => {
  event.preventDefault();
  // Chrome needs returnValue set to show the "leave site?" dialog
  event.returnValue = "";
};
let guardInstalled = false;

const syncUnloadGuard = () => {
  const active = entries.some(isActive);
  if (active && !guardInstalled) {
    window.addEventListener("beforeunload", onBeforeUnload);
    guardInstalled = true;
  } else if (!active && guardInstalled) {
    window.removeEventListener("beforeunload", onBeforeUnload);
    guardInstalled = false;
  }
};

const rememberInflight = () => {
  try {
    const names = entries.filter(isActive).map((entry) => entry.name);
    if (names.length === 0) {
      localStorage.removeItem(INTERRUPTED_KEY);
    } else {
      localStorage.setItem(INTERRUPTED_KEY, JSON.stringify(names));
    }
  } catch (e) {
    // storage blocked: the leave-page guard still works
  }
};

const emit = () => {
  syncUnloadGuard();
  rememberInflight();
  listeners.forEach((listener) => listener());
};

const patch = (id, changes) => {
  let touched = false;
  entries = entries.map((entry) => {
    if (entry.id !== id) return entry;
    touched = true;
    return { ...entry, ...changes };
  });
  if (touched) emit();
};

export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Stable reference between changes, as useSyncExternalStore needs. */
export const getEntries = () => entries;

export const summarize = (list) => {
  const summary = { total: list.length, active: 0, done: 0, failed: 0, progress: 0 };
  let progressSum = 0;
  for (const entry of list) {
    if (isActive(entry)) {
      summary.active++;
      progressSum += entry.progress;
    } else if (entry.status === STATUS.DONE) {
      summary.done++;
      progressSum += 1;
    } else {
      summary.failed++;
    }
  }
  const counted = summary.active + summary.done;
  summary.progress = counted === 0 ? 0 : progressSum / counted;
  return summary;
};

// ---- entries ----------------------------------------------------------------

const makeEntry = (file, scope, status) => ({
  id: nextId++,
  file,
  name: file.name,
  size: file.size,
  type: file.type || "",
  preview: PREVIEWABLE.includes(file.type) ? URL.createObjectURL(file) : null,
  scope,
  status,
  progress: 0,
  error: null,
  result: null,
  createdAt: Date.now(),
});

const readDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const run = (entry, target) => {
  const controller = new AbortController();
  controllers.set(entry.id, controller);
  const { signal } = controller;
  const { file, id } = entry;
  const { uploadUrl, additionalData, isEdit, setName, onUploaded, setShowPayModal } = target;

  const finish = (changes) => {
    controllers.delete(id);
    patch(id, changes);
  };

  return runQueue(async () => {
    if (signal.aborted) {
      finish({ status: STATUS.CANCELLED });
      return;
    }
    patch(id, { status: STATUS.UPLOADING, progress: 0 });

    let field;
    if (isEdit) {
      // the edit endpoint knows no upload_id; one base64 body, no progress
      field = { file_string: await readDataUrl(file) };
    } else {
      const upload = await uploadInChunks(file, {
        signal,
        onProgress: (fraction) => patch(id, { progress: fraction }),
      });
      if (!upload.ok) {
        if (upload.aborted) {
          finish({ status: STATUS.CANCELLED });
        } else if (upload.status === 413) {
          finish({ status: STATUS.TOO_BIG, error: "too big" });
        } else {
          finish({ status: STATUS.FAILED, error: `upload ${upload.status || "network"}` });
          ToastMessages.Error(`Upload of ${file.name} failed`);
        }
        return;
      }
      if (signal.aborted) {
        // cancelled between the last piece and the final call: nothing is saved
        discardUpload(upload.uploadId);
        finish({ status: STATUS.CANCELLED });
        return;
      }
      field = { upload_id: upload.uploadId };
    }

    patch(id, { status: STATUS.SAVING, progress: 1 });

    const body = {
      ...field,
      file_name: file.name,
      original_name: file.name,
      ...(setName ? { name: file.name } : {}),
      ...(additionalData || {}),
    };

    const response = isEdit
      ? await CrudApi.edit(uploadUrl, body, true)
      : await CrudApi.createNew(uploadUrl, body, setShowPayModal, true);

    if (response.status === 200) {
      finish({ status: STATUS.DONE, result: response.data });
      if (typeof onUploaded === "function") {
        try {
          onUploaded(response.data);
        } catch (e) {
          // a caller's list update must not mark the upload as failed
          console.error(e);
        }
      }
    } else {
      finish({ status: STATUS.FAILED, error: `save ${response.status || "network"}` });
      if (response.status !== 402 && response.status !== 413) {
        ToastMessages.Error(`Upload of ${file.name} failed`);
      }
    }
  });
};

/**
 * Starts an upload. `target` says where the file goes and what to do with
 * the API's answer; it is captured now, so the dropzone may unmount at will.
 *
 * @param {File} file
 * @param {string} scope which dropzone shows this entry (e.g. "filemanager")
 * @param {{uploadUrl: string, additionalData?: object, isEdit?: boolean, setName?: boolean, onUploaded?: (doc: any) => void, setShowPayModal?: Function|null}} target
 * @returns {number} entry id
 */
export const enqueue = (file, scope, target) => {
  const entry = makeEntry(file, scope, STATUS.QUEUED);
  const fullTarget = {
    additionalData: {},
    isEdit: false,
    setName: true,
    onUploaded: null,
    setShowPayModal: null,
    ...target,
  };
  targets.set(entry.id, fullTarget);
  entries = [...entries, entry];
  emit();
  run(entry, fullTarget);
  return entry.id;
};

/** Shows a file the dropzone refused (too big) next to the real uploads. */
export const addRejected = (file, scope, status = STATUS.TOO_BIG) => {
  const entry = makeEntry(file, scope, status);
  entries = [...entries, entry];
  emit();
  return entry.id;
};

export const cancel = (id) => {
  const controller = controllers.get(id);
  if (controller) controller.abort();
};

/** Re-runs a failed or cancelled upload with the same target. */
export const retry = (id) => {
  const entry = entries.find((candidate) => candidate.id === id);
  const target = targets.get(id);
  if (!entry || !target || isActive(entry) || entry.status === STATUS.DONE) return;
  patch(id, { status: STATUS.QUEUED, progress: 0, error: null });
  run({ ...entry, status: STATUS.QUEUED }, target);
};

const forget = (entry) => {
  if (entry.preview) URL.revokeObjectURL(entry.preview);
  targets.delete(entry.id);
  controllers.delete(entry.id);
};

/** Removes a finished entry from every view. Active uploads are cancelled first. */
export const dismiss = (id) => {
  const entry = entries.find((candidate) => candidate.id === id);
  if (!entry) return;
  if (isActive(entry)) cancel(id);
  forget(entry);
  entries = entries.filter((candidate) => candidate.id !== id);
  emit();
};

/** Removes every finished entry, of one scope or of all. */
export const clearFinished = (scope = null) => {
  const keep = [];
  for (const entry of entries) {
    if (isFinished(entry) && (scope === null || entry.scope === scope)) {
      forget(entry);
    } else {
      keep.push(entry);
    }
  }
  if (keep.length !== entries.length) {
    entries = keep;
    emit();
  }
};

/**
 * Names of uploads that were still running when the page was last unloaded
 * (tab closed, reload, navigation away). Stays until clearInterrupted() or
 * the next upload overwrites it, so a further reload still shows the notice.
 * @returns {string[]}
 */
export const readInterrupted = () => {
  try {
    const raw = localStorage.getItem(INTERRUPTED_KEY);
    const names = raw ? JSON.parse(raw) : [];
    return Array.isArray(names) ? names.filter((name) => typeof name === "string") : [];
  } catch (e) {
    return [];
  }
};

export const clearInterrupted = () => {
  try {
    localStorage.removeItem(INTERRUPTED_KEY);
  } catch (e) {
    // storage blocked
  }
};

const uploadManager = {
  STATUS,
  isActive,
  isFinished,
  subscribe,
  getEntries,
  summarize,
  enqueue,
  addRejected,
  cancel,
  retry,
  dismiss,
  clearFinished,
  readInterrupted,
  clearInterrupted,
};

export default uploadManager;
